import React, {useLayoutEffect, useRef} from "react";
import {C} from "../styles";
import ImageField from "./ImageField";
import {BACKGROUNDS} from "../landing/backgrounds";

// Formulaire généré à partir d'un registre de champs (rubriques intégrées et blocs libres de la landing).
// Contrôlé : `values` = données saisies, `defaults` = textes d'origine (affichés en grisé), `onChange(nextValues)`.
// Kinds gérés : text, textarea, url, title, youtubeId, choice, image, list (éléments répétables).

const URL_RE = /^(\/(?![/\\])|https:\/\/)\S*$/;
const YOUTUBE_ID_RE = /^[\w-]{6,40}$/;
const IMAGE_RE = /^\/uploads\/landing\/[0-9a-f]{32}\.webp$/;

// Message d'erreur d'une valeur de champ ("" = valide). Mêmes règles que le serveur (landingPageService) et le rendu.
export function fieldError(spec, value) {
    if (typeof value !== "string" || value === "") return "";
    if (spec.kind === "url" && !URL_RE.test(value)) return "Lien interne (/chemin) ou https:// uniquement";
    if (spec.kind === "youtubeId" && !YOUTUBE_ID_RE.test(value)) return "Identifiant YouTube invalide";
    if (spec.kind === "image" && !IMAGE_RE.test(value)) return "Image téléversée depuis l'Admin uniquement";
    if (spec.kind === "choice" && !spec.options.includes(value)) return "Valeur non autorisée";
    return "";
}

// true si au moins un champ (listes comprises) est invalide.
export function fieldsHaveErrors(fields, values) {
    return Object.entries(fields).some(([key, spec]) => {
        if (spec.kind !== "list") return Boolean(fieldError(spec, values[key]));
        return Array.isArray(values[key])
            && values[key].some(item => Object.entries(spec.itemFields).some(([k, itemSpec]) => Boolean(fieldError(itemSpec, item[k]))));
    });
}

// Données à enregistrer : sans champ vide (un champ vide = texte d'origine), sous-champs vides des listes retirés.
export function cleanFieldValues(values) {
    const data = {};
    for (const [key, value] of Object.entries(values)) {
        if (Array.isArray(value)) {
            // Éléments sans aucun texte écartés ; une liste vidée revient à la liste d'origine (clé absente).
            const items = value
                .map(item => Object.fromEntries(Object.entries(item).filter(([, v]) => v !== "")))
                .filter(item => Object.keys(item).length > 0);
            if (items.length > 0) data[key] = items;
        } else if (typeof value === "string" && value !== "") data[key] = value;
    }
    return data;
}

const CHOICE_LABELS = {
    signup: "Inscription", link: "Lien", primary: "Principal", ghost: "Discret", oui: "Oui", non: "Non",
    left: "À gauche", center: "Centré", none: "Aucune", fade: "Fondu", slide: "Glissement", "1": "1 colonne", "2": "2 colonnes",
    ...Object.fromEntries(BACKGROUNDS.map(b => [b.id, b.label])),
};

const inputStyle = {width: "100%", boxSizing: "border-box", padding: "7px 9px", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 14, fontFamily: "inherit"};
const smallButton = {border: `1px solid ${C.border}`, background: "#fff", borderRadius: 6, padding: "4px 9px", cursor: "pointer", fontSize: 13};

// Un champ texte long affiche tout son contenu : il grandit avec le texte (et quand la fenêtre est élargie ou rétrécie).
function useAutoGrow(ref, value) {
    const fitRef = useRef(null);
    // Ajuste la hauteur à chaque changement du texte…
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const fit = () => {
            if (!el.scrollHeight) return; // pas de mise en page (tests, élément masqué)
            el.style.height = "auto";
            el.style.height = `${el.scrollHeight + (el.offsetHeight - el.clientHeight)}px`;
        };
        fitRef.current = fit;
        fit();
    }, [ref, value]);
    // …et l'observateur de largeur n'est créé qu'une fois (pas à chaque frappe).
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === "undefined") return undefined;
        let width = el.clientWidth;
        const observer = new ResizeObserver(() => { if (el.clientWidth !== width) { width = el.clientWidth; fitRef.current?.(); } });
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref]);
}

// Les champs d'une ligne qui peuvent être longs deviennent une zone d'une ligne qui s'étend : le texte n'est plus coupé.
const isLongLine = (spec) => (spec.kind === "text" || spec.kind === "title" || spec.kind === "url") && spec.max > 60;

function TextControl({id, spec, value, placeholder, onChange, counterKey}) {
    const multiline = spec.kind === "textarea";
    const singleLineGrow = !multiline && isLongLine(spec);
    const Tag = multiline || singleLineGrow ? "textarea" : "input";
    const ref = useRef(null);
    useAutoGrow(ref, value);
    const length = value.length;
    return (
        <div>
            <Tag
                ref={ref}
                id={id}
                value={value}
                placeholder={placeholder}
                maxLength={spec.max}
                rows={multiline ? 4 : (singleLineGrow ? 1 : undefined)}
                // Une ligne reste une ligne : ni Entrée ni collage ne glissent de saut de ligne dans le texte.
                onKeyDown={singleLineGrow ? (e => { if (e.key === "Enter" && !e.nativeEvent.isComposing && e.keyCode !== 229) e.preventDefault(); }) : undefined}
                onChange={e => onChange(singleLineGrow ? e.target.value.replace(/[\r\n]+/g, " ") : e.target.value)}
                style={multiline || singleLineGrow ? {...inputStyle, resize: "vertical", overflowWrap: "anywhere"} : inputStyle}
            />
            <div style={{display: "flex", justifyContent: "space-between", fontSize: 12, marginTop: 2}}>
                <span style={{color: C.danger}}>{fieldError(spec, value)}</span>
                <span data-testid={`counter-${counterKey}`} data-at-limit={length >= spec.max ? "true" : "false"}
                      style={{color: length >= spec.max ? C.danger : C.textMuted}}>{length}/{spec.max}</span>
            </div>
        </div>
    );
}

function ChoiceControl({id, spec, value, onChange}) {
    return (
        <select id={id} value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
            <option value="">Par défaut</option>
            {spec.options.map(option => <option key={option} value={option}>{CHOICE_LABELS[option] || option}</option>)}
        </select>
    );
}

function FieldControl({id, spec, value, placeholder, onChange, counterKey}) {
    if (spec.kind === "choice") return <ChoiceControl id={id} spec={spec} value={value} onChange={onChange}/>;
    if (spec.kind === "image") {
        return (
            <div>
                <ImageField value={value} onChange={onChange}/>
                <span style={{color: C.danger, fontSize: 12}}>{fieldError(spec, value)}</span>
            </div>
        );
    }
    return <TextControl id={id} spec={spec} value={value} placeholder={placeholder} onChange={onChange} counterKey={counterKey}/>;
}

export default function LandingFields({fields, values, defaults = {}, idBase, onChange}) {
    const setValue = (key, value) => onChange({...values, [key]: value});
    const clear = (key) => { const next = {...values}; delete next[key]; onChange(next); };
    const listOfKey = (key) => (Array.isArray(values[key]) ? values[key] : (defaults[key] || []));

    function renderList(key, spec) {
        const items = listOfKey(key);
        const custom = Array.isArray(values[key]);
        const replace = (next) => setValue(key, next);
        const move = (i, delta) => {
            const next = [...items];
            const [moved] = next.splice(i, 1);
            next.splice(i + delta, 0, moved);
            replace(next);
        };
        return (
            <fieldset key={key} style={{border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, margin: "0 0 14px"}}>
                <legend style={{fontWeight: 600}}>{spec.label}</legend>
                {items.map((item, i) => (
                    <div key={i} data-testid={`list-item-${key}`} style={{borderBottom: `1px dashed ${C.border}`, paddingBottom: 8, marginBottom: 8}}>
                        {Object.entries(spec.itemFields).map(([k, itemSpec]) => {
                            const id = `${idBase}-${key}-${i}-${k}`;
                            return (
                                <div key={k} style={{marginBottom: 6}}>
                                    {itemSpec.kind !== "image" && <label htmlFor={id} style={{display: "block", fontSize: 13}}>{`${itemSpec.label} ${i + 1}`}</label>}
                                    <FieldControl
                                        id={id} spec={itemSpec} value={item[k] || ""} counterKey={`${key}-${i}-${k}`}
                                        onChange={v => replace(items.map((it, j) => (j === i ? {...it, [k]: v} : it)))}
                                    />
                                </div>
                            );
                        })}
                        <div style={{display: "flex", gap: 6}}>
                            <button type="button" style={smallButton} aria-label={`Monter ${spec.label} ${i + 1}`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                            <button type="button" style={smallButton} aria-label={`Descendre ${spec.label} ${i + 1}`} disabled={i === items.length - 1} onClick={() => move(i, 1)}>↓</button>
                            <button type="button" style={smallButton} aria-label={`Supprimer ${spec.label} ${i + 1}`} onClick={() => replace(items.filter((_, j) => j !== i))}>Supprimer</button>
                        </div>
                    </div>
                ))}
                <div style={{display: "flex", gap: 8}}>
                    <button type="button" style={smallButton} aria-label={`Ajouter à ${spec.label}`} disabled={items.length >= spec.maxItems}
                            onClick={() => replace([...items, Object.fromEntries(Object.keys(spec.itemFields).map(k => [k, ""]))])}>+ Ajouter</button>
                    {custom && (
                        <button type="button" style={smallButton} aria-label={`Rétablir la liste d'origine ${spec.label}`} onClick={() => clear(key)}>Rétablir la liste d'origine</button>
                    )}
                    <span style={{fontSize: 12, alignSelf: "center"}}>{items.length}/{spec.maxItems}</span>
                </div>
            </fieldset>
        );
    }

    return (
        <>
            {Object.entries(fields).map(([key, spec]) => {
                if (spec.kind === "list") return renderList(key, spec);
                const id = `${idBase}-${key}`;
                const value = typeof values[key] === "string" ? values[key] : "";
                return (
                    <div key={key} style={{marginBottom: 12}}>
                        <div style={{display: "flex", justifyContent: "space-between", alignItems: "baseline"}}>
                            <label htmlFor={id} style={{fontWeight: 600, fontSize: 14}}>{spec.label}</label>
                            {value !== "" && (
                                <button type="button" style={smallButton} aria-label={`Rétablir ${spec.label}`} onClick={() => clear(key)}>Rétablir le texte d'origine</button>
                            )}
                        </div>
                        {spec.kind === "title" && (
                            <div style={{fontSize: 12, color: C.textMuted}}>Entourez un mot de {"{ }"} pour le mettre en valeur.</div>
                        )}
                        <FieldControl id={id} spec={spec} value={value} placeholder={defaults[key]} counterKey={key} onChange={v => (spec.kind === "choice" && v === "" ? clear(key) : setValue(key, v))}/>
                    </div>
                );
            })}
        </>
    );
}
